import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Auth } from '../Auth'
import LanguageSwitcher from '../LanguageSwitcher'
import styles from './LandingPage.module.css'

interface Feature {
    id: string
    icon: string
    video: string
}

const FEATURES: Feature[] = [
    {
        id: 'scan',
        icon: '📷',
        video: '/videos/scan.mp4',
    },
    {
        id: 'management',
        icon: '📚',
        video: '/videos/management.mp4',
    },
    {
        id: 'interface',
        icon: '⚡',
        video: '/videos/interface.mp4',
    },
    {
        id: 'enrichment',
        icon: '🌐',
        video: '/videos/enrichment.mp4',
    },
    {
        id: 'export',
        icon: '📤',
        video: '/videos/export.mp4',
    },
    {
        id: 'import',
        icon: '📥',
        video: '/videos/import.mp4',
    },
    {
        id: 'biblionautas',
        icon: '👩‍🚀',
        video: '/videos/biblionautas.mp4',
    },
    {
        id: 'biblioverso',
        icon: '🌌',
        video: '/videos/biblioverso.mp4',
    },
    {
        id: 'lists',
        icon: '📝',
        video: '/videos/lists.mp4',
    },
]

interface LandingPageProps {
    readonly isAuthenticated: boolean
    readonly onNavigateToLibrary: () => void
}

export function LandingPage({ isAuthenticated, onNavigateToLibrary }: Readonly<LandingPageProps>) {
    const { t } = useTranslation()
    const [showAuth, setShowAuth] = useState(false)

    if (showAuth && !isAuthenticated) {
        return (
            <div className={styles.landingAuthWrapper}>
                <button
                    className={styles.btnGhost}
                    onClick={() => setShowAuth(false)}
                    style={{ position: 'absolute', top: '1rem', left: '1rem', zIndex: 100 }}
                >
                    {t('landing.nav.back')}
                </button>
                <Auth />
            </div>
        )
    }

    return (
        <div className={styles.landingContainer}>
            <header className={styles.landingHeader}>
                <nav className={styles.landingNav}>
                    <div className={styles.landingBrand}>
                        <span className={styles.landingBrandIcon}>📚</span>
                        <span>Bibliotelia</span>
                    </div>

                    <div className={styles.landingAuthButtons}>
                        <div style={{ marginRight: '1rem' }}>
                            <LanguageSwitcher showTitle={false} compact={true} />
                        </div>
                        {isAuthenticated ? (
                            <button onClick={onNavigateToLibrary} className={`${styles.btn} ${styles.btnPrimary}`}>
                                {t('landing.nav.library')}
                            </button>
                        ) : (
                            <>
                                <button onClick={() => setShowAuth(true)} className={`${styles.btn} ${styles.btnGhost}`} data-testid="login-button">
                                    {t('landing.nav.login')}
                                </button>
                                <button onClick={() => setShowAuth(true)} className={`${styles.btn} ${styles.btnPrimary}`} data-testid="signup-button">
                                    {t('landing.nav.signup')}
                                </button>
                            </>
                        )}
                    </div>
                </nav>
            </header>

            <main>
                <section className={styles.landingHero}>
                    <div className={`${styles.landingBlob} ${styles.blob1}`}></div>
                    <div className={`${styles.landingBlob} ${styles.blob2}`}></div>

                    <div className={`${styles.heroContent} animate-fade-in-up`}>
                        <span className={styles.heroBadge}>{t('landing.hero.badge')}</span>
                        <h1 className={styles.heroTitle}>
                            <Trans i18nKey="landing.hero.title" />
                        </h1>
                        <p className={styles.heroSubtitle}>
                            <Trans i18nKey="landing.hero.subtitle" />
                        </p>

                        <div className={styles.heroButtons}>
                            {isAuthenticated ? (
                                <button onClick={onNavigateToLibrary} className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLarge}`}>
                                    {t('landing.hero.explore')}
                                </button>
                            ) : (
                                <button onClick={() => setShowAuth(true)} className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLarge}`}>
                                    {t('landing.hero.start')}
                                </button>
                            )}
                        </div>
                    </div>
                </section>

                <section className={styles.landingFeatures}>
                    <div className={styles.featuresGrid}>
                        {FEATURES.map((feature, index) => (
                            <div key={feature.id} className={`${styles.featureCard} stagger-${(index % 3) + 1} animate-fade-in-up`}>
                                <div className={styles.featureCardInner}>
                                    <div className={styles.featureCardFront}>
                                        <span className={styles.featureIcon}>{feature.icon}</span>
                                        <h3 className={styles.featureTitle}>{t(`landing.features.${feature.id}.title`)}</h3>
                                        <p className={styles.featureDescription}>{t(`landing.features.${feature.id}.description`)}</p>
                                    </div>
                                    <div className={styles.featureCardBack}>
                                        <div className={styles.videoContainer}>
                                            <video src={feature.video} autoPlay loop muted playsInline className={styles.featureVideo} />
                                            <div className={styles.featureVideoOverlay}>
                                                <span className={styles.featureIconSmall}>{feature.icon}</span>
                                                <h4>{t(`landing.features.${feature.id}.title`)}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            <footer className={styles.landingFooter}>
                <p>{t('landing.footer', { year: new Date().getFullYear() })}</p>
            </footer>
        </div>
    )
}
